import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HardDrive } from "lucide-react";
import {
  MAIL_STORAGE_FOLDER_DEFAULT,
  MAIL_STORAGE_FOLDER_MAX,
  MAIL_STORAGE_FOLDER_MIN,
} from "@/lib/constants/mailStorage";
import { downloadFolderToServerStorage } from "@/lib/storage/folderManualStorageSync";
import { getFolderStorageProgress } from "@/lib/storage/folderStorageProgress";
import { fetchMailStoredUids } from "@/lib/api/storage";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import { showMailToast } from "@/lib/ui/mailToast";
import { cn } from "@/lib/cn";

type Props = {
  open: boolean;
  accountId: number;
  folder: string;
  folderLabel: string;
  uids: number[];
  onClose: () => void;
  onDone?: (stored: number) => void;
};

type LaunchSnapshot = {
  accountId: number;
  folder: string;
  folderLabel: string;
  uids: number[];
};

export function MailFolderStorageDialog({
  open,
  accountId,
  folder,
  folderLabel,
  uids,
  onClose,
  onDone,
}: Props) {
  const launchRef = useRef<LaunchSnapshot | null>(null);
  const [count, setCount] = useState(MAIL_STORAGE_FOLDER_DEFAULT);
  const [includeAttachments, setIncludeAttachments] = useState(true);
  const [alreadyStored, setAlreadyStored] = useState(0);
  const [pendingCount, setPendingCount] = useState<number | null>(null);

  const availableUids = useMemo(
    () => [...new Set(uids.filter((uid) => uid > 0))],
    [uids]
  );

  const snapshot = launchRef.current;
  const displayFolderLabel = snapshot?.folderLabel ?? folderLabel;
  const isInbox = navFolderSlot(snapshot?.folder ?? folder, displayFolderLabel) === "inbox";

  const maxSelectable =
    pendingCount == null
      ? Math.min(MAIL_STORAGE_FOLDER_MAX, availableUids.length)
      : pendingCount > 0
        ? Math.min(MAIL_STORAGE_FOLDER_MAX, pendingCount)
        : 0;
  const minSelectable =
    maxSelectable === 0
      ? 0
      : Math.min(MAIL_STORAGE_FOLDER_MIN, maxSelectable);

  useEffect(() => {
    if (!open) {
      launchRef.current = null;
      return;
    }

    launchRef.current = {
      accountId,
      folder,
      folderLabel,
      uids: availableUids,
    };

    setCount(MAIL_STORAGE_FOLDER_DEFAULT);
    setAlreadyStored(0);
    setPendingCount(null);

    let cancelled = false;
    void fetchMailStoredUids(accountId, folder).then((res) => {
      if (cancelled || !res.ok || !res.data.success) {
        if (!cancelled) setPendingCount(0);
        return;
      }
      const set = new Set(res.data.uids);
      const pending = availableUids.filter((uid) => !set.has(uid));
      const stored = availableUids.length - pending.length;
      setAlreadyStored(stored);
      setPendingCount(pending.length);
      const nextMax = Math.min(MAIL_STORAGE_FOLDER_MAX, pending.length);
      setCount(
        pending.length > 0
          ? Math.min(nextMax, Math.max(Math.min(MAIL_STORAGE_FOLDER_MIN, pending.length), MAIL_STORAGE_FOLDER_DEFAULT))
          : 0
      );
    });

    return () => {
      cancelled = true;
    };
  }, [open, accountId, folder, folderLabel, availableUids]);

  const handleConfirm = useCallback(() => {
    const launch = launchRef.current;
    if (!launch || launch.uids.length === 0 || maxSelectable === 0) return;
    onClose();
    void downloadFolderToServerStorage(launch.accountId, launch.folder, {
      count,
      includeAttachments,
      uids: launch.uids,
    }).then((stored) => {
      const progress = getFolderStorageProgress(launch.accountId);
      if (progress.error) {
        showMailToast(progress.error, "error");
      } else if (stored > 0) {
        showMailToast(
          `${stored} e-mail${stored > 1 ? "s" : ""} copié${stored > 1 ? "s" : ""} sur le serveur.`,
          "success"
        );
      } else {
        showMailToast("Tous les e-mails visibles sont déjà copiés.", "info");
      }
      onDone?.(stored);
    });
  }, [count, includeAttachments, maxSelectable, onClose, onDone]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/40 p-4 sm:items-center"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] p-5 shadow-xl"
        role="dialog"
        aria-labelledby="folder-storage-title"
        aria-modal="true"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--mail-accent)]/10 text-[var(--mail-accent)]">
            <HardDrive className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="folder-storage-title" className="text-base font-semibold text-[var(--mail-text)]">
              Copier ce dossier sur le serveur
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[var(--mail-muted)]">
              {displayFolderLabel} — copies sur le serveur Aurélia, accessibles depuis tous vos
              appareils. La copie continue en arrière-plan si vous changez de dossier.
            </p>
          </div>
        </div>

        {isInbox ? (
          <p className="mt-4 rounded-lg bg-[var(--mail-surface-hover)] px-3 py-2 text-[11px] text-[var(--mail-muted)]">
            La réception se synchronise automatiquement (200 derniers). Ce téléchargement manuel
            complète les messages déjà visibles.
          </p>
        ) : null}

        <div className="mt-4 space-y-4">
          <label className="block space-y-2">
            <span className="text-xs font-medium text-[var(--mail-text)]">
              Nombre de messages ({minSelectable}–{maxSelectable || MAIL_STORAGE_FOLDER_MAX})
            </span>
            <input
              type="range"
              min={minSelectable}
              max={Math.max(minSelectable, maxSelectable)}
              value={Math.min(count, maxSelectable || minSelectable || 0)}
              disabled={availableUids.length === 0 || maxSelectable === 0}
              onChange={(e) => setCount(Number(e.target.value))}
              className="w-full accent-[var(--mail-accent)]"
            />
            <div className="flex justify-between text-[11px] text-[var(--mail-muted)]">
              <span>{Math.min(count, maxSelectable || 0)} message(s)</span>
              <span>
                {pendingCount == null
                  ? `${availableUids.length} visible(s)`
                  : pendingCount > 0
                    ? `${pendingCount} à copier · ${availableUids.length} visible(s)`
                    : `${availableUids.length} visible(s)`}
              </span>
            </div>
          </label>

          <label className="flex cursor-pointer items-center gap-2 text-xs text-[var(--mail-text)]">
            <input
              type="checkbox"
              checked={includeAttachments}
              onChange={(e) => setIncludeAttachments(e.target.checked)}
              className="rounded border-[var(--mail-border-soft)]"
            />
            Inclure les pièces jointes
          </label>

          {alreadyStored > 0 ? (
            <p className="text-[11px] text-[var(--mail-muted)]">
              {alreadyStored} déjà copié{alreadyStored > 1 ? "s" : ""} — la prochaine copie prend les{" "}
              {Math.min(count, pendingCount || count)} suivant{Math.min(count, pendingCount || count) > 1 ? "s" : ""}{" "}
              non archivé{Math.min(count, pendingCount || count) > 1 ? "s" : ""}.
            </p>
          ) : pendingCount != null && pendingCount > 0 ? (
            <p className="text-[11px] text-[var(--mail-muted)]">
              Copie les messages visibles les plus récents non encore archivés.
            </p>
          ) : pendingCount === 0 && availableUids.length > 0 ? (
            <p className="text-[11px] text-[var(--mail-muted)]">
              Tous les messages visibles sont déjà copiés sur le serveur.
            </p>
          ) : null}
        </div>

        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[var(--mail-border-soft)] px-4 py-2 text-xs font-medium text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => void handleConfirm()}
            disabled={availableUids.length === 0 || maxSelectable === 0}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg bg-[var(--mail-accent)] px-4 py-2 text-xs font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            )}
          >
            Télécharger
          </button>
        </div>
      </div>
    </div>
  );
}